import React, { useEffect, useRef, useState } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const EBookView = () => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const observersSetupRef = useRef(false);
  const printStyleAddedRef = useRef(false);
  const workerLoaded = useRef(false);

  // Load PDF URL from localStorage on component mount
  useEffect(() => {
    const savedUrl = localStorage.getItem("url");
    if (savedUrl) {
      const decryptedData = atob(atob(atob(atob(atob(atob(savedUrl))))));
      setPdfUrl(decryptedData);

      // Set cleanup timer
      const timer = setTimeout(() => {
        localStorage.removeItem("url");
      }, 120000);

      return () => clearTimeout(timer);
    }
  }, []);

  // Function to remove toolbar buttons
  const removeToolbarButtons = () => {
    console.log("Attempting to remove toolbar buttons");
    const selectors = [
      '[data-testid="get-file__download-button"]',
      '[data-testid="open__button"]',
      '[data-testid="print__button"]',
      '[data-testid="toolbar__more-actions-popover-target"]',
    ];

    let buttonsFound = false;
    selectors.forEach((selector) => {
      const buttons = document.querySelectorAll(selector);

      buttons.forEach((button) => {
        buttonsFound = true;
        const parentDiv = button.closest(".rpv-toolbar__item");

        if (parentDiv && !parentDiv.getAttribute("removed")) {
          parentDiv.setAttribute("removed", "true");
          parentDiv.style.display = "none";
          console.log(`Removed button: ${selector}`);
        }
      });
    });

    return buttonsFound;
  };

  // Print protection
  useEffect(() => {
    if (printStyleAddedRef.current) return;

    // Override print function
    const originalPrint = window.print;
    window.print = function () {
      console.log("Print attempt blocked");
      return false;
    };

    // Add print media styles to prevent printing
    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        body { display: none !important; }
        canvas { display: none !important; }
        .rpv-core__viewer { display: none !important; }
        * { visibility: hidden !important; }
      }
    `;
    document.head.appendChild(style);
    printStyleAddedRef.current = true;

    return () => {
      window.print = originalPrint;
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Protection for keyboard shortcuts and context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (
        isCtrlOrCmd &&
        (e.key === "p" || e.key === "s" || (e.shiftKey && e.key === "s"))
      ) {
        console.log("Blocked keyboard shortcut:", e.key);
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("contextmenu", handleContextMenu, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, []);

  // Setup observers for dynamically added content
  useEffect(() => {
    if (!pdfUrl || observersSetupRef.current) return;

    // Try multiple times with increasing delays
    const attemptSetup = (attempts = 0) => {
      if (attempts > 10) return; // Give up after 10 attempts

      const hasButtons = removeToolbarButtons();

      if (hasButtons) {
        console.log("Successfully found and removed toolbar buttons");
        setupObservers();
      } else {
        console.log(`Setup attempt ${attempts + 1} failed, trying again soon`);
        setTimeout(() => attemptSetup(attempts + 1), 500);
      }
    };

    // Start attempts after a short delay to allow initial render
    setTimeout(() => attemptSetup(), 200);

    function setupObservers() {
      if (observersSetupRef.current) return;
      observersSetupRef.current = true;
      console.log("Setting up mutation observers");

      // Observer for toolbar changes
      const observer = new MutationObserver(() => {
        clearTimeout(window.toolbarTimeout);
        window.toolbarTimeout = setTimeout(removeToolbarButtons, 100);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Observer for canvas protection
      const canvasObserver = new MutationObserver(() => {
        clearTimeout(window.canvasTimeout);
        window.canvasTimeout = setTimeout(() => {
          document.querySelectorAll("canvas").forEach(protectCanvas);
        }, 100);
      });

      canvasObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
        canvasObserver.disconnect();
        clearTimeout(window.toolbarTimeout);
        clearTimeout(window.canvasTimeout);
      };
    }
  }, [pdfUrl]);

  // Function to protect a canvas element
  const protectCanvas = (canvas) => {
    if (!canvas || canvas.getAttribute("protected")) return;

    canvas.setAttribute("protected", "true");
    canvas.style.userSelect = "none";
    canvas.style.webkitUserSelect = "none";

    // Prevent right-click and dragging
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    canvas.addEventListener("dragstart", (e) => e.preventDefault());

    const context = canvas.getContext("2d");
    if (context && !context._secured) {
      context._secured = true;

      // Protect getImageData
      const originalGetImageData = context.getImageData;
      context.getImageData = function () {
        console.log("Image data access attempted");
        return originalGetImageData.apply(this, arguments);
      };

      // Protect drawImage
      const originalDrawImage = context.drawImage;
      context.drawImage = function () {
        originalDrawImage.apply(this, arguments);

        if (window.matchMedia("print").matches) {
          this.fillStyle = "rgba(255, 255, 255, 0.95)";
          this.fillRect(0, 0, canvas.width, canvas.height);
          this.font = "24px Arial";
          this.fillStyle = "rgba(0, 0, 0, 0.5)";
          this.fillText("Printing Not Allowed", 50, canvas.height / 2);
        }
      };
    }
  };

  // Ensure the PDF worker is available in the deployed environment
  const getWorkerUrl = () => {
    // Try different possible paths based on environment
    const possiblePaths = [
      "/pdf.worker.min.js",
      `${window.location.origin}/pdf.worker.min.js`,
      `${process.env.PUBLIC_URL}/pdf.worker.min.js`,
      "https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js" // Fallback to CDN
    ];

    // You could implement more sophisticated detection logic here
    return possiblePaths[0]; // Start with the first option
  };

  return (
    <>
      {pdfUrl ? (
        <div style={{ height: "95vh" }}>
          <Worker workerUrl={getWorkerUrl()}>
            <Viewer
              fileUrl={pdfUrl}
              plugins={[defaultLayoutPluginInstance]}
              onDocumentLoad={() => {
                // Additional attempt to remove buttons after document loads
                setTimeout(removeToolbarButtons, 1000);
              }}
              renderExternalRenderer={(props) => {
                if (props.canvasRef.current) {
                  protectCanvas(props.canvasRef.current);
                }
                return null;
              }}
            />
          </Worker>
        </div>
      ) : (
        <div>Please select a PDF to view</div>
      )}
    </>
  );
};

export default EBookView;